/** @prettier */
import { Reducer, useReducer, useEffect, useCallback } from "react";
import {
    TestData,
    getTestAndQuestions,
    QuestionData,
    startTest,
    pauseTest,
    selectAnswer,
    deselectAnswer,
    toggleFlaggedQuestion,
    finishTest,
    sendHeartbeat,
} from "./testRunnerApi";
import { useHistory } from "react-router-dom";
import produce from "immer";
import { assert, assertNever } from "../../util/assert";
import PersistentStore from "../../util/PersistentStore";
import { useInterval } from "../../util/useInterval";
import {
    useQuestionImages,
    ImageLoadActions,
    ImagesLoadState,
} from "../View/useQuestionImages";

const isNumber = (x: unknown): x is number => typeof x === "number";
const currentQuestionCache = new PersistentStore("current-question", isNumber);

// Server heartbeat expiration is 120000, using slightly less than 1/4th so that
//  we have four attempts before the server disconnects us
const TEST_HEARTBEAT_INTERVAL = 29000;

function getAtIndex<T>(arr: T[], index: number) {
    return arr[index] as T | undefined;
}

type TestType =
    | { type: "STUDY" }
    | { type: "TIMED"; deadline: Date }
    | { type: "UNTIMED" };
const buildTestType = (test: TestData): TestType => {
    if (test.mode === "TIMED") {
        return {
            type: "TIMED",
            deadline: new Date(Date.now() + test.timeRemainingInSeconds * 1000),
        };
    }
    return { type: test.mode };
};

export type QuestionDataWithImages = QuestionData & ImagesLoadState;
export type TestRunnerLoadedState = {
    state: "loaded";
    testType: TestType;
    questions: QuestionDataWithImages[];
    // Adding as its own state, so we don't have to refactor away from
    // questions.length in case of paged data
    totalQuestionCount: number;
    currentQuestionIndex: number;
    testName: string;
    timeExpired: boolean;
};

export type TestRunnerState =
    | TestRunnerLoadedState
    | { state: "loading"; id: string };

type TestRunnerSyncAction =
    | {
          event: "testDataLoad";
          id: string;
          testData: TestData;
          questions: QuestionData[];
          currentQuestion?: number;
      }
    | {
          event: "selectQuestion";
          index: number | "next" | "prev";
      }
    | { event: "gotoReview" }
    | {
          event: "selectAnswer";
          questionId?: string; // set by async dispatch
          answer: QuestionData["selectedAnswer"];
      }
    | {
          event: "toggleFlagged";
          questionId?: string; //set by async dispatch
      }
    | ImageLoadActions
    // has both a sync and async effect
    | { event: "timeExpired" };
type TestRunnerAsyncActions = { event: "pauseTest" } | { event: "finishTest" };
export type TestRunnerDispatch = React.Dispatch<
    TestRunnerSyncAction | TestRunnerAsyncActions
>;

const isValidIndex = (number: number, arr: { length: number }) =>
    number >= 0 && number < arr.length;

const testStateReducer: Reducer<
    TestRunnerState,
    TestRunnerSyncAction
> = produce((state: TestRunnerState, action: TestRunnerSyncAction) => {
    if (action.event === "testDataLoad") {
        return state.state === "loading" && action.id === state.id
            ? ({
                  state: "loaded",
                  testType: buildTestType(action.testData),
                  questions: action.questions,
                  totalQuestionCount: action.questions.length,
                  currentQuestionIndex: action.currentQuestion ?? 0,
                  testName: action.testData.name,
                  timeExpired: false,
              } as const)
            : state;
    }
    if (state.state === "loading") {
        return state;
    }
    if (action.event === "selectQuestion") {
        let newIndex =
            action.index === "next"
                ? state.currentQuestionIndex + 1
                : action.index === "prev"
                ? state.currentQuestionIndex - 1
                : action.index;
        if (
            isValidIndex(newIndex, state.questions) ||
            newIndex === state.questions.length
        ) {
            state.currentQuestionIndex = newIndex;
        }
        return;
    }
    if (action.event === "gotoReview") {
        state.currentQuestionIndex = state.questions.length;
        return;
    }
    if (action.event === "timeExpired") {
        state.timeExpired = true;
        return;
    }
    /* Actions that target a specific question */
    assert(action.questionId, "Expected to have a questionId");
    const question = state.questions.find(({ id }) => id === action.questionId);
    assert(question, `Expected a question with id - ${action.questionId}`);

    if (action.event === "selectAnswer") {
        question.selectedAnswer =
            action.answer === "none" ||
            isValidIndex(action.answer, question.answerChoices)
                ? action.answer
                : "none";
        return;
    }
    if (action.event === "toggleFlagged") {
        question.flagged = !question.flagged;
        return;
    }
    if (action.event === "loadingImages") {
        question.images = { state: "loading" };
        return;
    }
    if (action.event === "loadedImages") {
        question.images = {
            state: "loaded",
            stemImages: action.stemImages,
            explanationImages: [],
        };
        return;
    }
    return assertNever(action, `Unhandled action ${action}`);
});

// =========
// Selectors
// =========
export type SummaryState = Record<"answered" | "flagged", boolean>;
export const getSummaryState = (state: TestRunnerLoadedState) =>
    state.questions.map((q) => ({
        flagged: q.flagged,
        answered: q.selectedAnswer !== "none",
    }));
export const getCurrentQuestion = (
    state: TestRunnerLoadedState,
): QuestionDataWithImages | undefined => {
    return state.questions[state.currentQuestionIndex];
};
export const getAnsweredQuestionCount = (state: TestRunnerLoadedState) =>
    getSummaryState(state).filter(({ answered }) => answered).length;

// ====
// Hook
// ====
export const useTestRunnerState = (
    testId: string,
): [TestRunnerState, TestRunnerDispatch] => {
    const [state, syncDispatch] = useReducer(testStateReducer, {
        state: "loading",
        id: testId,
    });
    const history = useHistory();
    const dispatch = useCallback(
        async (action: TestRunnerAsyncActions | TestRunnerSyncAction) => {
            // Would be nice if we didn't need the state to look up the question
            //  but would require passing more data to, e.g. keyboard shortcuts
            const getCurrentQuestion = () => {
                assert(state.state === "loaded", "Expected loaded state");
                return getAtIndex(state.questions, state.currentQuestionIndex);
            };
            // NOTE add other actions after the timeExpired check so they can't be
            //  dispatched when time has run out
            if (action.event === "finishTest") {
                await finishTest(testId);
                history.push(`/tests/${testId}/results`);
                return;
            }
            if (action.event === "timeExpired") {
                // ignore duplicate events, block other actions.
                if (!(state.state === "loaded" && !state.timeExpired)) {
                    return;
                }
                // display the "time is expired" message for three seconds
                setTimeout(() => {
                    dispatch({ event: "finishTest" });
                }, 3000);
            }
            if (state.state === "loaded" && state.timeExpired === true) {
                // ignore actions other than "timeExpired" or "finishTest" when time has run out
                return;
            }
            if (action.event === "pauseTest") {
                await pauseTest(testId);
                history.push("/tests");
                return;
            }
            if (action.event === "selectAnswer") {
                const question = getCurrentQuestion();
                if (!question) return;
                // used by sync dispatcher, avoids race due to selecting a different question
                action.questionId = question.id;
                if (action.answer !== "none") {
                    await selectAnswer(
                        testId,
                        question.id,
                        question.answerChoices[action.answer],
                    );
                } else {
                    await deselectAnswer(testId, question.id);
                }
            }
            if (action.event === "toggleFlagged") {
                const question = getCurrentQuestion();
                if (!question) return;
                // used by sync dispatcher, avoids race due to selecting a different question
                action.questionId = question.id;
                await toggleFlaggedQuestion(testId, question.id);
            }
            syncDispatch(action);
        },
        [testId, history, state],
    );

    useQuestionImages(state, syncDispatch);

    useEffect(() => {
        const questionPromise = getTestAndQuestions(testId);
        const startTestPromise = startTest(testId);
        const cachedCurrentQuestion = currentQuestionCache.get(testId);
        Promise.all([questionPromise, startTestPromise])
            .then(([{ questions, ...testData }]) => {
                syncDispatch({
                    event: "testDataLoad",
                    id: testId,
                    testData,
                    questions,
                    currentQuestion: cachedCurrentQuestion ?? undefined,
                });
            })
            .catch((e) => {
                console.error("Error starting the test", e);
                // TODO: toast message?
                history.push("/tests");
            });
        // Be sure not to use dispatch here, or we'll send a lot of unnecessary API calls!
    }, [testId, history]);

    // Cache the currentQuestionIndex in localStorage
    const currentQuestionIndex =
        state.state === "loaded" ? state.currentQuestionIndex : undefined;
    useEffect(() => {
        if (currentQuestionIndex !== undefined) {
            currentQuestionCache.set(testId, currentQuestionIndex);
        }
    }, [testId, currentQuestionIndex]);

    const isTimedTest =
        state.state === "loaded" && state.testType.type === "TIMED";
    useInterval(
        () => {
            sendHeartbeat().catch((e) => {
                console.error("Error sending heartbeat");
            });
        },
        // Setting the interval to null disables the interval
        isTimedTest ? TEST_HEARTBEAT_INTERVAL : null,
    );

    return [state, dispatch];
};
